import { useState, useEffect, useRef } from "react";
import { Pause, Play, ShoppingCart, RefreshCw, XCircle, Loader2, Eye, Copy, QrCode } from "lucide-react";
import logo from "@/assets/logo.webp";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import RenovarDialog from "@/components/RenovarDialog";
import AnuncioViewsDialog from "@/components/AnuncioViewsDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

interface OwnerActionButtonsProps {
  anuncioId: string;
  anuncioStatus?: string;
  variant?: "card" | "inline";
  expiraEm?: string | null;
  isDestaque?: boolean;
  destaqueInicio?: string | null;
  sessionContato?: string;
  sessionToken?: string;
  actionLoading: string | null;
  setActionLoading: (v: string | null) => void;
  onAnuncioAction?: () => void;
  onAnuncioRefresh?: () => void;
  renovacaoPendente?: boolean;
  itemNome?: string;
  itemImageUrl?: string;
  /** When true, encerrar dialog shows batch cancel messaging */
  isBatchStacked?: boolean;
  batchCount?: number;
}

const OwnerActionButtons = ({
  anuncioId,
  anuncioStatus,
  variant = "card",
  expiraEm,
  isDestaque,
  destaqueInicio,
  sessionContato,
  sessionToken,
  actionLoading,
  setActionLoading,
  onAnuncioAction,
  onAnuncioRefresh,
  renovacaoPendente,
  itemNome,
  itemImageUrl,
  isBatchStacked,
  batchCount,
}: OwnerActionButtonsProps) => {
  const [renovarOpen, setRenovarOpen] = useState(false);
  const [viewsOpen, setViewsOpen] = useState(false);
  const [confirmSoldOpen, setConfirmSoldOpen] = useState(false);
  const [encerrarOpen, setEncerrarOpen] = useState(false);
  const [encerrarMotivo, setEncerrarMotivo] = useState("");
  const [pixQrDialog, setPixQrDialog] = useState<{ qrCode: string; qrCodeBase64: string; valorTaxa?: number; duracaoDias?: number; anuncioId?: string } | null>(null);
  const [generatingPayment, setGeneratingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Poll for payment confirmation when PIX QR dialog is open
  useEffect(() => {
    if (!pixQrDialog?.anuncioId) {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      return;
    }
    setPaymentConfirmed(false);
    const aid = pixQrDialog.anuncioId;
    let attempts = 0;
    const maxAttempts = 120; // 4 minutes at 2s intervals

    const poll = async () => {
      attempts++;
      if (attempts > maxAttempts) {
        if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
        return;
      }
      try {
        if (!sessionContato || !sessionToken) return;
        const { data } = await supabase.functions.invoke("client-anuncios", {
          body: { contato: sessionContato, token: sessionToken },
        });
        const anuncios = data?.anuncios || data || [];
        const found = Array.isArray(anuncios) && anuncios.find((a: any) => a.id === aid);
        if (found?.taxa_paga) {
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
          setPaymentConfirmed(true);
          setTimeout(() => {
            setPixQrDialog(null);
            setPaymentConfirmed(false);
            toast({ title: "✅ Pagamento confirmado!", description: "Sua renovação está sendo processada." });
            (onAnuncioRefresh || onAnuncioAction)?.();
          }, 2000);
        }
      } catch { /* ignore */ }
    };

    poll(); // immediate first check
    pollingRef.current = setInterval(poll, 2000);
    return () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };
  }, [pixQrDialog?.anuncioId]);

  const status = anuncioStatus || "aprovado";
  const isTerminal = ["rejeitado", "vendido", "encerrado"].includes(status);
  const vigente = !expiraEm || new Date(expiraEm) > new Date();
  const isExpiredByDate = !!(expiraEm && new Date(expiraEm) <= new Date()) && status !== "vendido";
  const disableAllActions = isExpiredByDate && status !== "pendente" && status !== "aguardando_pagamento";

  const canPause = status === "aprovado" && vigente;
  const canResume = status === "pausado";
  const canMarkSold = ["aprovado", "pausado"].includes(status);
  // Renovação já aprovada que agendou destaque para o fim do período atual:
  // o anúncio ainda não entrou na fase de destaque — bloquear nova renovação até começar.
  const hasScheduledDestaque = !!(isDestaque && destaqueInicio && new Date(destaqueInicio).getTime() > Date.now());
  const canRenew = !renovacaoPendente && !hasScheduledDestaque && (["expirado", "aprovado", "pausado"].includes(status) || (!vigente && !isTerminal));
  const canEncerrar = ["aprovado", "pausado", "pendente", "aguardando_pagamento"].includes(status);

  const callAction = async (acao: string, label: string, extra: Record<string, unknown> = {}) => {
    if (!sessionContato || !sessionToken) {
      toast({ title: "Faça login para gerenciar", variant: "destructive" });
      return;
    }
    setActionLoading(acao);
    if (acao === "renovar" && extra.metodo_pagamento !== "coins") setGeneratingPayment(true);
    try {
      const { data, error } = await supabase.functions.invoke("client-anuncio-action", {
        body: { contato: sessionContato, token: sessionToken, anuncio_id: anuncioId, acao, ...extra },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.gateway === "mercadopago" && data?.pix_qr_code_base64) {
        setPixQrDialog({ qrCode: data.pix_qr_code, qrCodeBase64: data.pix_qr_code_base64, valorTaxa: data.valor_cobrado ?? data.valor_taxa, duracaoDias: data.duracao_dias, anuncioId });
        toast({ title: "QR Code PIX gerado!", description: "Escaneie o QR Code ou copie o código PIX." });
        // Don't dismiss the card — keep it visible so the dialog stays mounted
      } else if (data?.checkout_url) {
        toast({ title: "Redirecionando para pagamento...", description: "Após o pagamento, sua renovação será analisada pelo administrador." });
        window.open(data.checkout_url, "_blank");
      } else if (acao === "renovar") {
        toast({ title: "Renovação solicitada!", description: "Você será notificado assim que o administrador aprovar a renovação." });
        (onAnuncioRefresh || onAnuncioAction)?.();
      } else {
        toast({ title: `Anúncio ${label} com sucesso!` });
        onAnuncioAction?.();
      }
    } catch (err: any) {
      toast({ title: err.message || `Erro ao ${label.toLowerCase()}`, variant: "destructive" });
    }
    setActionLoading(null);
    setGeneratingPayment(false);
  };

  const handleAction = async (e: React.MouseEvent, acao: string, label: string) => {
    e.stopPropagation();
    e.preventDefault();

    if (disableAllActions) {
      return;
    }

    if (acao === "views") {
      setViewsOpen(true);
      return;
    }

    if (!sessionContato || !sessionToken) {
      toast({ title: "Faça login para gerenciar", variant: "destructive" });
      return;
    }

    if (acao === "renovar") {
      setRenovarOpen(true);
      return;
    }

    if (acao === "vendido") {
      setConfirmSoldOpen(true);
      return;
    }

    if (acao === "encerrar") {
      setEncerrarOpen(true);
      setEncerrarMotivo("");
      return;
    }

    callAction(acao, label);
  };

  const handleRenovarConfirm = async (
    duracao_config_id: string,
    metodo_pagamento: string,
    comprovante?: File,
    destaque?: boolean,
    personagem_pagamento_coins?: string,
  ) => {
    let comprovante_coins_url: string | undefined;
    if (metodo_pagamento === "coins" && comprovante) {
      setActionLoading("renovar");
      const ext = comprovante.name.split(".").pop() || "png";
      const path = `${anuncioId}_renew_${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("comprovantes-coins").upload(path, comprovante);
      if (upErr) {
        toast({ title: "Erro ao enviar comprovante", variant: "destructive" });
        setActionLoading(null);
        return;
      }
      const { data: urlData } = supabase.storage.from("comprovantes-coins").getPublicUrl(path);
      comprovante_coins_url = urlData.publicUrl;
    }
    callAction("renovar", "renovado", {
      duracao_config_id,
      metodo_pagamento,
      comprovante_coins_url,
      destaque: !!destaque,
      personagem_pagamento_coins: metodo_pagamento === "coins" ? (personagem_pagamento_coins || null) : null,
    });
  };


  const isPending = status === "pendente" || status === "aguardando_pagamento";

  const buttons = isPending
    ? [
        canEncerrar && { acao: "encerrar", label: "encerrado", hint: "Cancelar", icon: XCircle, color: "text-destructive hover:text-destructive/80" },
      ].filter(Boolean) as { acao: string; label: string; hint: string; icon: any; color: string; disabled?: boolean }[]
    : [
        { acao: "views", label: "Desempenho", hint: "Desempenho", icon: Eye, color: "text-cyan-400 hover:text-cyan-300" },
        canPause && { acao: "pausar", label: "pausado", hint: "Pausar", icon: Pause, color: "text-yellow-400 hover:text-yellow-300" },
        canResume && { acao: "retomar", label: "retomado", hint: "Retomar", icon: Play, color: "text-green-400 hover:text-green-300" },
        canMarkSold && { acao: "vendido", label: "marcado como vendido", hint: "Vendido", icon: ShoppingCart, color: "text-blue-400 hover:text-blue-300" },
        canRenew && { acao: "renovar", label: "renovado", hint: status === "pausado" ? "Ative o anúncio para poder renovar" : "Renovar", icon: RefreshCw, color: "text-primary hover:text-primary/80", disabled: status === "pausado" },
        hasScheduledDestaque && { acao: "renovar", label: "renovado", hint: "Renovação indisponível enquanto o destaque agendado não inicia", icon: RefreshCw, color: "text-primary hover:text-primary/80", disabled: true },
        canEncerrar && { acao: "encerrar", label: "encerrado", hint: "Encerrar", icon: XCircle, color: "text-destructive hover:text-destructive/80" },
      ].filter(Boolean) as { acao: string; label: string; hint: string; icon: any; color: string; disabled?: boolean }[];

  if (buttons.length === 0) return <div className="border-t border-border/40 py-3" />;

  const wrapperClass = variant === "card"
    ? `border-t border-border/40 flex items-center justify-around py-1.5 px-1 ${disableAllActions ? "opacity-50" : ""}`
    : `flex items-center gap-1 ${disableAllActions ? "opacity-50" : ""}`;

  return (
    <>
      <div className={wrapperClass}>
        {buttons.map(({ acao, label, hint, icon: Icon, color, disabled }) => (
          <Tooltip key={acao} delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                onClick={(e) => handleAction(e, acao, label)}
                disabled={!!actionLoading || disableAllActions || !!disabled}
                className={`p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 ${color}`}
              >
                {actionLoading === acao ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Icon className="w-3.5 h-3.5" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="z-[100]">{hint}</TooltipContent>
          </Tooltip>
        ))}
      </div>
      <RenovarDialog
        open={renovarOpen}
        onOpenChange={setRenovarOpen}
        onConfirm={handleRenovarConfirm}
        isDestaque={isDestaque}
        itemNome={itemNome}
        itemImageUrl={itemImageUrl}
        expiraEm={expiraEm}
      />
      <AnuncioViewsDialog
        open={viewsOpen}
        onOpenChange={setViewsOpen}
        anuncioId={anuncioId}
        sessionContato={sessionContato}
        sessionToken={sessionToken}
      />
      <AlertDialog open={confirmSoldOpen} onOpenChange={setConfirmSoldOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar venda</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja marcar {itemNome ? <strong>"{itemNome}"</strong> : "este item"} como vendido? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => callAction("vendido", "marcado como vendido")}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={encerrarOpen} onOpenChange={(o) => { if (!o) setEncerrarOpen(false); }}>
        <DialogContent className="tibia-border bg-card max-w-sm" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-pixel text-xs text-destructive">
              Encerrar Anúncio
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Informe o motivo do encerramento. Esta ação não pode ser desfeita.
              <span className="block mt-1 text-destructive/80 font-medium">O encerramento não gera reembolso do valor pago no anúncio.</span>
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={encerrarMotivo}
            onChange={(e) => setEncerrarMotivo(e.target.value)}
            placeholder="Motivo do encerramento..."
            maxLength={500}
            rows={3}
            className="w-full rounded border border-border bg-background px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setEncerrarOpen(false)}
              className="tibia-tab rounded-sm px-4 py-1.5 font-pixel text-[8px]"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                if (!encerrarMotivo.trim()) {
                  toast({ title: "Informe o motivo do encerramento", variant: "destructive" });
                  return;
                }
                setEncerrarOpen(false);
                callAction("encerrar", "encerrado", { motivo: encerrarMotivo.trim() });
              }}
              disabled={!!actionLoading || !encerrarMotivo.trim()}
              className="tibia-btn rounded-sm px-4 py-1.5 font-pixel text-[8px] flex items-center gap-1 disabled:opacity-50"
            >
              {actionLoading === "encerrar" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
              Encerrar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loading dialog while generating payment */}
      <Dialog open={generatingPayment} onOpenChange={() => {}}>
        <DialogContent className="max-w-xs" hideCloseButton onClick={(e) => e.stopPropagation()} onPointerDownOutside={(e) => e.stopPropagation()} onInteractOutside={(e) => e.stopPropagation()}>
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
              <img src={logo} alt="RPG Coins" className="absolute inset-0 m-auto w-8 h-8 rounded-full object-contain" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-foreground">Gerando link de pagamento...</p>
              <p className="text-xs text-muted-foreground">Aguarde um momento</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* MercadoPago PIX QR Code Dialog */}
      <Dialog open={!!pixQrDialog} onOpenChange={(o) => { if (!o && !paymentConfirmed) setPixQrDialog(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-sm sm:max-w-md flex flex-col gap-4 overflow-hidden" onClick={(e) => e.stopPropagation()} onPointerDownOutside={(e) => e.stopPropagation()} onInteractOutside={(e) => e.stopPropagation()}>
          {paymentConfirmed ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-bold text-green-500">Pagamento Confirmado!</p>
                <p className="text-xs text-muted-foreground">Sua renovação está sendo processada.</p>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-sm">
                  <QrCode className="w-4 h-4 text-primary" />
                  Aguardando Pagamento de Renovação
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Escaneie o QR Code ou copie o código PIX para pagar.
                </DialogDescription>
              </DialogHeader>
              {pixQrDialog?.qrCodeBase64 && (
                <div className="flex flex-col gap-3 min-w-0 w-full">
                  {itemNome && (
                    <div className="flex items-center justify-center gap-2 min-w-0">
                      <img
                        src={itemImageUrl || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(itemNome.replace(/ /g, "_"))}`}
                        alt={itemNome}
                        className="w-7 h-7 object-contain shrink-0"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                      />
                      <span className="text-xs font-medium text-foreground truncate">{itemNome}</span>
                    </div>
                  )}
                  {typeof pixQrDialog.valorTaxa === "number" && (
                    <p className="text-center font-pixel text-sm text-primary font-bold">
                      R$ {pixQrDialog.valorTaxa.toFixed(2)}
                    </p>
                  )}
                  <div className="flex justify-center">
                    <div className="relative">
                      <img
                        src={`data:image/png;base64,${pixQrDialog.qrCodeBase64}`}
                        alt="QR Code PIX"
                        className="w-44 h-44"
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md">
                          <img src={logo} alt="RPG Coins" className="w-6 h-6 rounded-full object-cover" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-sm font-bold text-primary">Escaneie o QR Code para pagar</p>
                  {pixQrDialog.qrCode && (
                    <div className="flex flex-col gap-1 min-w-0 w-full">
                      <p className="text-xs text-muted-foreground text-center">Ou copie o código PIX:</p>
                      <div className="flex items-center gap-2 border border-border rounded-md px-3 py-2 min-w-0 w-full overflow-hidden">
                        <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">{pixQrDialog.qrCode}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(pixQrDialog.qrCode);
                            toast({ title: "Código PIX copiado!" });
                          }}
                          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium whitespace-nowrap shrink-0"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          Copiar
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 py-1">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Aguardando confirmação...</span>
                  </div>
                  <div className="bg-primary/10 border border-primary/30 rounded-md p-2 text-center">
                    <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-1">
                      💡 Não feche esta janela até o pagamento ser confirmado.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OwnerActionButtons;
